<template>
  <div class="public-attachment-container">
    <div class="public-attachment-card">
      <TracktorLogoSection />

      <div class="public-attachment-body">
        <div class="public-header">
          <div class="public-file-title">
            <h2>{{ filename }}</h2>
          </div>
          <div class="locale-switcher" aria-label="Language">
            <a :href="enUrl" :class="{ 'is-active': locale === 'en' }">EN</a>
            <a :href="ruUrl" :class="{ 'is-active': locale === 'ru' }">RU</a>
          </div>
        </div>

        <div v-if="flashAlert" class="flash">{{ flashAlert }}</div>

        <h1 v-if="status !== 'available'" class="file-title">{{ statusTitle }}</h1>
        <template v-else>
          <p class="file-meta">
            {{ humanSize }}
            <span v-if="expiresAtText"> · {{ expiresAtText }}</span>
          </p>

          <p class="muted">{{ labels.storedIn }}</p>

          <form
            v-if="passwordRequired"
            :action="authenticateUrl"
            method="post"
            class="password-form"
          >
            <input type="hidden" name="authenticity_token" :value="csrfToken" />
            <p class="muted">{{ labels.passwordRequired }}</p>
            <input
              class="password-input"
              type="text"
              name="password"
              :placeholder="labels.password"
              autocomplete="one-time-code"
              autocapitalize="off"
              spellcheck="false"
            />
            <button type="submit" class="btn">{{ labels.unlock }}</button>
          </form>

          <div v-else class="actions">
            <a :href="downloadUrl" class="btn">{{ labels.download }}</a>
            <a :href="dashboardUrl" class="btn btn-secondary">{{ labels.openDashboard }}</a>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import TracktorLogoSection from '@/components/common/TracktorLogoSection.vue'

const props = defineProps({
  locale: {
    type: String,
    required: true,
  },
  enUrl: {
    type: String,
    required: true,
  },
  ruUrl: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  filename: {
    type: String,
    default: '',
  },
  humanSize: {
    type: String,
    default: '',
  },
  expiresAtText: {
    type: String,
    default: '',
  },
  flashAlert: {
    type: String,
    default: '',
  },
  passwordRequired: {
    type: Boolean,
    default: false,
  },
  authenticateUrl: {
    type: String,
    default: '',
  },
  downloadUrl: {
    type: String,
    default: '',
  },
  dashboardUrl: {
    type: String,
    required: true,
  },
  csrfToken: {
    type: String,
    default: '',
  },
  labels: {
    type: Object,
    required: true,
  },
})

const statusTitle = computed(() => {
  if (props.status === 'revoked') return props.labels.revoked
  if (props.status === 'expired') return props.labels.expired
  return ''
})
</script>

<style scoped>
.public-attachment-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.public-attachment-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  max-width: 720px;
  width: 100%;
  overflow: hidden;
}

.public-attachment-body {
  padding: 32px;
}

.public-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.public-file-title {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  gap: 8px;
  color: #333;
  font-size: 18px;
  font-weight: 700;
}

.public-file-title span {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.public-file-icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  color: #333;
}

.locale-switcher {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
  font-size: 14px;
}

.locale-switcher a {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 999px;
  color: #2aabee;
  text-decoration: none;
}

.locale-switcher .is-active {
  background: #2aabee;
  border-color: #2aabee;
  color: #fff;
}

.flash {
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 16px;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #991b1b;
}

.file-title {
  margin: 0 0 8px;
  color: #333;
  font-size: 22px;
  overflow-wrap: anywhere;
}

.file-meta {
  margin: 0 0 20px;
  color: #666;
}

.muted {
  color: #666;
  line-height: 1.5;
}

.password-form {
  display: grid;
  gap: 12px;
  margin-top: 20px;
}

.password-input {
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  -webkit-text-security: disc;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  background: #2aabee;
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
}

@media (max-width: 640px) {
  .public-attachment-container {
    padding: 12px;
  }

  .public-attachment-body {
    padding: 24px 20px;
  }

  .public-header {
    align-items: flex-start;
  }
}
</style>
